import React, { useRef, useState } from 'react';
import { Box, Button, CircularProgress, Modal, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import WorkOutlined from '@mui/icons-material/WorkOutlined';
import { getAll } from '../../../../services/MissionService';
import { addProcess } from '../../../../services/ProcessService';
import { useParams } from 'react-router-dom';
import { commonGridSx, gridLocaleText, makeGridToolbar } from '../../../../utils/gridStyles';
import { emptyRowSelection, getSelectedRowIds } from '../../../../utils/gridSelection';
import { DataGrid } from '../../../../components/SortableDataGrid/SortableDataGrid';

const CustomToolbar = makeGridToolbar('Missions');

export default function MissionModal({ snackbarShowMessage, processes, reloadData, setReloadData }) {
  const [open, setOpen] = useState(false);
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState(emptyRowSelection);
  const [loadingToAdd, setLoadingToAdd] = useState(false);
  const { idCandidat } = useParams();
  const active = useRef(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const buildParams = () => {
    const params = new URLSearchParams();
    params.append('statut', '910f357b-0b13-4f66-89a8-5c06964c1dcf');
    return params;
  };

  const allowedSteps = ['Non validée', 'Refusé par client', 'Refusé par candidat', 'Refusé par Go&Dev', 'Integration'];

  const isRowSelectable = (params) => {
    const missionId = params.row.uuid;
    const blockingProcess = (processes || []).find(
      (process) => process.missionId === missionId && !allowedSteps.includes(process.step)
    );
    return !blockingProcess;
  };

  // With "select all", only keep the missions that can actually be selected.
  const getSelectedMissionIds = () =>
    getSelectedRowIds(rowSelectionModel, rows.filter((row) => isRowSelectable({ row })));

  const addProcesses = () => {
    const selectedMissionIds = getSelectedMissionIds();
    if (selectedMissionIds.length > 0 && !loadingToAdd) {
      (async () => {
        snackbarShowMessage('Ajout des process en cours...', 'warning');
        setLoadingToAdd(true);
        const data = {
          candidat: [idCandidat ?? localStorage.getItem('idCandidat')],
          missions: selectedMissionIds,
        };
        try {
          const response = await addProcess(data);
          if (!active.current) return;
          snackbarShowMessage(response.data.msg);
          setLoadingToAdd(false);
          handleClose();
          setReloadData(!reloadData);
        } catch (error) {
          if (!active.current) return;
          snackbarShowMessage(error.response?.data?.error?.detail, 'error', 4000);
          setLoadingToAdd(false);
        }
      })();
    }
  };

  React.useEffect(() => {
    let isActive = true;
    active.current = true;
    if (open) {
      (async () => {
        setLoading(true);
        try {
          const reponse = await getAll(buildParams());
          if (!isActive) return;
          setMissions(reponse.data.missions || []);
        } catch (error) {
          if (!isActive) return;
          snackbarShowMessage(error.response?.data?.error?.detail, 'error', 4000);
        } finally {
          if (isActive) setLoading(false);
        }
      })();
    }
    return () => { isActive = false; active.current = false; };
  }, [open]);

  const columns = [
    {
      field: 'numMission', headerName: 'N°', width: 80,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value || '—'}</Typography>,
    },
    {
      field: 'client', headerName: 'CLIENT', width: 180,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'nom_mission', headerName: 'NOM MISSION', flex: 1, minWidth: 200,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'contact', headerName: 'CONTACT', width: 160,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value || '—'}</Typography>,
    },
  ];

  const rows = missions.map((m) => ({ ...m, id: m.uuid }));

  return (
    <>
      <Button
        variant="contained"
        size="small"
        onClick={handleOpen}
        startIcon={<Add />}
        sx={{ background: '#03407b', color: '#fff', height: '2.5em', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#02357a' } }}
      >
        Ajouter une mission
      </Button>
      <Modal
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        closeAfterTransition
        open={open}
        onClose={handleClose}
      >
        <Box sx={{ background: '#fff', borderRadius: '16px', p: 3, width: '800px', maxWidth: '95vw', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: 2, background: '#ebf4fb', color: '#03407b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <WorkOutlined sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#0f172a' }}>Ajouter le candidat à une mission</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>
                Cochez une ou plusieurs missions actives : un process est créé pour chacune. Les missions où le candidat a déjà un process en cours ne sont pas sélectionnables.
              </Typography>
            </Box>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} sx={{ color: '#03407b' }} />
            </Box>
          ) : (
            <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', height: '400px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.id}
                checkboxSelection
                isRowSelectable={isRowSelectable}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={(model) => setRowSelectionModel(model)}
                pageSizeOptions={[50]}
                disableRowSelectionOnClick={false}
                showToolbar
                slots={{ toolbar: CustomToolbar }}
                slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
                localeText={gridLocaleText}
                sx={commonGridSx}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={handleClose}
              sx={{ background: '#3b82f6', color: '#fff', '&:hover': { background: '#2563eb' } }}>
              Annuler
            </Button>
            <Button variant="contained" onClick={addProcesses} disabled={getSelectedMissionIds().length === 0 || loadingToAdd}
              sx={{ background: '#22c55e', color: '#fff', '&:hover': { background: '#16a34a' } }}>
              {getSelectedMissionIds().length > 1 ? `Ajouter aux ${getSelectedMissionIds().length} missions` : 'Ajouter à la mission'}
              {loadingToAdd && <CircularProgress size={16} sx={{ ml: 1, color: '#fff' }} />}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
